// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { ReactNode } from 'react';
import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import BoxHeadline from 'presentations/views/components/molecules/BoxHeadline';
import Button from 'presentations/views/components/atoms/Button';
import EventTypeHeader from 'presentations/views/components/organisms/EventTypeHeader';
import EventLocationList from 'presentations/views/components/organisms/EventLocationList';
import { borderRadius, mediaMax, spacing } from 'presentations/views/styles';
import ServiceContentBox from 'presentations/views/components/templates/ServiceContentBox';
import EventSummary from 'presentations/views/components/organisms/EventSummary';
import { gray05, gray06, white } from 'presentations/views/styles/color';
import EventMainVisual from 'presentations/views/components/organisms/EventMainVisual';
import EventAddCalendar from 'presentations/views/components/organisms/EventAddCalendar';
import { splitHostAndUsersFromEvent } from 'presentations/views/helpers/event';
import EventContact from 'presentations/views/components/organisms/EventContact';
import Progress from 'presentations/views/components/atoms/Progress';
import { useSharedEventPage } from './hooks';
import Snackbar, { SeverityType } from '../../atoms/Snackbar';
import { SharedEventDetailItem } from 'presentations/api/schema/event';
import { HostOperationType } from 'domains/models/event_type/hostOperationType';
import {
  isTeamPlanAvailable,
  PlanId,
} from 'domains/models/subscription/planId';
import dynamic from 'next/dynamic';
import Subscription from 'domains/models/subscription';
import {
  guestEventCancellationPath,
  guestEventReschedulingsPath,
} from 'presentations/routes/path';
import { Status } from 'domains/models/event/status';
import { formatHostName } from 'presentations/views/helpers/formatter';
const EventTypeServiceRegistration = dynamic(() =>
  import(
    'presentations/views/components/organisms/EventTypeServiceRegistration'
  ),
);

interface Props {
  breadcrumbs: ReactNode;
  mapApiKey: string;
  event: SharedEventDetailItem;
  planId: PlanId;
  isShowPromotion: boolean;
  isShowHeaderTeamImage: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state, actions } = useSharedEventPage(props);
  let host = null;
  if (state.event) {
    const users = splitHostAndUsersFromEvent(state.event.users);
    host = users.host;
  }
  const teamImage = state.viewer?.team?.profileImage || null;
  const teamName =
    host &&
    props.event.team &&
    formatHostName(
      props.event.hostOperationType,
      host.name,
      props.event.team?.name,
    );
  const headerTeamImage = props.isShowHeaderTeamImage
    ? props.event.team?.headerTeamImage
    : null;
  const hostImage =
    props.event.hostOperationType === HostOperationType.Team
      ? props.event.team?.profileImage
      : host?.profileImage;

  const isTeam = isTeamPlanAvailable(props.planId);
  const isShowAd = !isTeam || Boolean(props.isShowPromotion);

  return (
    <ServicePageTemplate
      header={
        <EventTypeHeader
          viewer={state.viewer}
          headerTeamImage={headerTeamImage}
        />
      }
      isHiddenFooter
      isHiddenContentFooter={true}
      breadcrumbs={props.breadcrumbs}
    >
      {state.event ? (
        <>
          <PageContainer>
            <MainContainer>
              <BgWrapper>
                <EventMainVisual coverImage={state.event.coverImage} />
                <EventSummaryContainer>
                  <EventSummary
                    event={state.event}
                    hostImage={hostImage}
                    teamImage={teamImage}
                  />
                  {[Status.Open, Status.Request].includes(state.event.status) &&
                    Boolean(state.event.isEnableGuestUpdate) && (
                      <ActionButtonContainer>
                        <ActionButton
                          href={guestEventCancellationPath(state.event.uid)}
                          color="primary"
                          variant="outlined"
                          withoutNextLink
                        >
                          {t('Cancel')}
                        </ActionButton>
                        {state.event.status === Status.Open && (
                          <ActionButton
                            href={guestEventReschedulingsPath(state.event.uid)}
                            color="primary"
                            variant="outlined"
                            withoutNextLink
                          >
                            {t('Reschedule')}
                          </ActionButton>
                        )}
                      </ActionButtonContainer>
                    )}
                </EventSummaryContainer>
                <ServiceContentBox noVerticalPadding>
                  {state.eventContact && (
                    <>
                      <Hr />
                      <EventContact
                        viewer={state.viewer}
                        event={state.event}
                        eventContact={state.eventContact}
                        isShowNotice={true}
                      />
                    </>
                  )}
                  <Hr />
                  <EventAddCalendar viewer={state.viewer} event={state.event} />
                </ServiceContentBox>
              </BgWrapper>
              {state.event.locationId && (
                <ServiceContentBox>
                  <HeadlineContainer>
                    <BoxHeadline iconName="place">
                      {t('locationTitle')}
                    </BoxHeadline>
                  </HeadlineContainer>
                  <EventLocationList
                    viewer={state.viewer}
                    event={state.event}
                    teamName={teamName}
                    mapApiKey={props.mapApiKey}
                    locationId={state.event.locationId}
                    access={state.event.access}
                    address={state.event.address}
                    phoneNumber={state.event.phoneNumber}
                    customLocation={state.event.customLocation}
                    url={state.event.url}
                  />
                </ServiceContentBox>
              )}
            </MainContainer>
          </PageContainer>
        </>
      ) : (
        <ProgressContainer>
          <Progress />
        </ProgressContainer>
      )}
      {state.errorMessage && (
        <Snackbar
          onClose={actions.onCloseMessage}
          isOpen={true}
          severity={SeverityType.Error}
          message={state.errorMessage}
        />
      )}
      {isShowAd && <EventTypeServiceRegistration />}
    </ServicePageTemplate>
  );
}

const PageContainer = styled.div`
  background-color: ${gray06};
  padding: ${spacing(48)} ${spacing(24)};
  ${mediaMax.tablet} {
    width: 100%;
    padding: ${spacing(24)};
  }
  ${mediaMax.smartphone} {
    padding: ${spacing(24)} ${spacing(12)};
  }
`;

const MainContainer = styled.div`
  position: relative;
  max-width: 960px;
  margin: 0 auto;
`;

const BgWrapper = styled.div`
  background-color: ${white};
  border-radius: ${borderRadius(16)};
  overflow: hidden;
  margin-bottom: ${spacing(40)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(20)};
  }
`;

const HeadlineContainer = styled.div`
  margin-bottom: ${spacing(24)};
`;

export const Hr = styled.div`
  height: 1px;
  margin: ${spacing(32)} 0;
  background-color: ${gray05};
`;

const ProgressContainer = styled.div`
  width: 100%;
  height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EventSummaryContainer = styled.div`
  flex: 1;
  padding: 0 ${spacing(48)};
`;
const ActionButtonContainer = styled.div`
  margin-top: ${spacing(16)};
`;
const ActionButton = styled(Button)`
  margin-right: ${spacing(16)};
  background: ${white};
  :last-of-type {
    margin-right: 0;
  }

  ${mediaMax.tablet} {
    max-width: 240px;
    margin-right: ${spacing(8)};
  }
  ${mediaMax.smartphone} {
    max-width: 100%;
  }
`;
